import { TGTAuthClient } from "@tgtone/auth-sdk";

/**
 * Holder para la instancia real de TGTAuthClient.
 * Se sincroniza desde AuthContext con la instancia activa de useTGTAuth.
 * El proxy no crea clientes ni administra la sesión por su cuenta.
 */
let _authClient: TGTAuthClient | null = null;

const safeMethods = new Set([
  "getToken", "getStoredToken", "getStoredRefreshToken",
  "isRedirecting", "isSessionMonitorActive", "isHeartbeatActive",
  "getTenantId", "getRoles", "hasRole", "hasAccessToApp",
  "getCurrentAppKey", "getBlockedRedirectUrl",
]);

/**
 * Proxy lazy que delega todas las llamadas a la instancia real una vez registrada.
 * Las importaciones existentes de `authClient` en services etc. siguen funcionando
 * porque la referencia al proxy es constante — solo el target interno cambia.
 */
export const authClient = new Proxy({} as TGTAuthClient, {
  get(_, prop: string | symbol) {
    // Evitar que JS runtime trate el proxy como thenable
    if (prop === "then" || prop === "catch" || prop === "finally") return undefined;

    if (!_authClient) {
      if (typeof prop === "string" && safeMethods.has(prop)) {
        // Métodos seguros que no deberían explotar si no hay cliente
        return (..._args: unknown[]) => {
          if (prop === "getToken" || prop === "getStoredToken" || prop === "getStoredRefreshToken") return null;
          if (prop === "isRedirecting" || prop === "isSessionMonitorActive" || prop === "isHeartbeatActive") return false;
          if (prop === "getTenantId") return null;
          if (prop === "getRoles" || prop === "hasRole" || prop === "hasAccessToApp") return () => false;
          if (prop === "getCurrentAppKey") return null;
          if (prop === "getBlockedRedirectUrl") return () => "/login";
          return undefined;
        };
      }
      throw new Error(
        "TGTAuthClient no inicializado. ¿AuthContext no está montado? " +
        "Asegúrate de que <AuthProvider> envuelva el árbol de componentes."
      );
    }

    const value = (_authClient as unknown as Record<string | symbol, unknown>)[prop];
    return typeof value === "function" ? value.bind(_authClient) : value;
  },
});

/**
 * Sincroniza la instancia activa creada por useTGTAuth.
 * Reemplazar la referencia es necesario si React vuelve a montar el provider;
 * conservar un cliente desmontado deja a los interceptores leyendo una sesión obsoleta.
 */
export function registerAuthClient(client: TGTAuthClient): void {
  if (_authClient === client) return;
  _authClient = client;
  console.log("[Auth] TGTAuthClient activo sincronizado");
}

/**
 * Obtiene la instancia real de TGTAuthClient. Lanza si no está inicializada.
 * Útil para código que necesita acceso directo al cliente real (no proxy).
 */
export function getAuthClient(): TGTAuthClient {
  if (!_authClient) {
    throw new Error("TGTAuthClient no inicializado");
  }
  return _authClient;
}
